import { useState, useEffect } from "react";
import { 
  Users, Calendar, DollarSign, TrendingUp, CheckCircle, Clock, 
  AlertCircle, Search, Filter, RefreshCw, Eye, Edit, Trash2,
  ChevronDown, ChevronRight, Lock
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  // Dashboard data
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [enhancementLeads, setEnhancementLeads] = useState([]);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  
  // Selected booking for details
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoggingIn(true);
    
    try {
      await axios.post(`${API}/admin/login`, { password });
      setIsAuthenticated(true);
      toast.success("Login successful");
      loadDashboardData();
    } catch (error) {
      toast.error("Invalid password");
    } finally {
      setIsLoggingIn(false);
    }
  };

  const loadDashboardData = async () => {
    setIsLoading(true);
    try {
      const [statsRes, bookingsRes, customersRes, leadsRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard`),
        axios.get(`${API}/admin/bookings`),
        axios.get(`${API}/admin/customers`),
        axios.get(`${API}/enhancement-leads`)
      ]);
      
      setStats(statsRes.data);
      setBookings(bookingsRes.data.bookings);
      setCustomers(customersRes.data.customers);
      setEnhancementLeads(leadsRes.data.leads);
    } catch (error) {
      toast.error("Failed to load dashboard data");
    } finally {
      setIsLoading(false);
    }
  };

  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      await axios.patch(`${API}/bookings/${bookingId}/status?status=${newStatus}`);
      toast.success("Booking status updated");
      loadDashboardData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const filteredBookings = bookings.filter(booking => {
    const matchesStatus = statusFilter === "all" || booking.status === statusFilter;
    const matchesSearch = !searchTerm || 
      booking.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.customer_email.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const getStatusBadge = (status) => {
    const variants = {
      pending: "bg-yellow-100 text-yellow-800",
      confirmed: "bg-blue-100 text-blue-800",
      in_progress: "bg-purple-100 text-purple-800",
      completed: "bg-green-100 text-green-800",
      cancelled: "bg-red-100 text-red-800"
    };
    return variants[status] || "bg-slate-100 text-slate-800";
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="pt-20 min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="bg-white p-8 border border-slate-200 w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#1a365d] rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock size={32} className="text-white" />
            </div>
            <h1 className="heading-lg text-[#0A0A0A]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Admin Access
            </h1>
            <p className="text-slate-600 mt-2">Enter password to continue</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-field"
                placeholder="Enter admin password"
                data-testid="admin-password-input"
              />
            </div>
            <Button 
              type="submit" 
              className="w-full btn-primary"
              disabled={isLoggingIn}
              data-testid="admin-login-btn"
            >
              {isLoggingIn ? "Logging in..." : "Login"}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen bg-slate-100">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="heading-lg text-[#0A0A0A]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Admin Dashboard
            </h1>
            <p className="text-slate-600">Manage bookings, customers, and leads</p>
          </div>
          <Button 
            onClick={loadDashboardData}
            variant="outline"
            className="flex items-center gap-2"
            data-testid="refresh-btn"
          >
            <RefreshCw size={16} />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white p-6 border border-slate-200" data-testid="stat-total-bookings">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 uppercase tracking-wider">Total Bookings</p>
                  <p className="text-3xl font-bold text-[#0A0A0A]">{stats.total_bookings}</p>
                </div>
                <Calendar className="text-[#1a365d]" size={32} />
              </div>
            </div>
            
            <div className="bg-white p-6 border border-slate-200" data-testid="stat-confirmed">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 uppercase tracking-wider">Confirmed</p>
                  <p className="text-3xl font-bold text-[#1a365d]">{stats.confirmed_bookings}</p>
                </div>
                <CheckCircle className="text-blue-500" size={32} />
              </div>
            </div>
            
            <div className="bg-white p-6 border border-slate-200" data-testid="stat-deposits">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 uppercase tracking-wider">Deposits Collected</p>
                  <p className="text-3xl font-bold text-green-600">${stats.total_deposits}</p>
                </div>
                <DollarSign className="text-green-500" size={32} />
              </div>
            </div>
            
            <div className="bg-white p-6 border border-slate-200" data-testid="stat-leads">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 uppercase tracking-wider">Enhancement Leads</p>
                  <p className="text-3xl font-bold text-purple-600">{stats.enhancement_leads}</p>
                </div>
                <TrendingUp className="text-purple-500" size={32} />
              </div>
            </div>
          </div>
        )}

        {/* Main Content Tabs */}
        <Tabs defaultValue="bookings" className="space-y-6">
          <TabsList className="bg-white border border-slate-200 p-1">
            <TabsTrigger value="bookings" className="data-[state=active]:bg-[#1a365d] data-[state=active]:text-white">
              Bookings
            </TabsTrigger>
            <TabsTrigger value="customers" className="data-[state=active]:bg-[#1a365d] data-[state=active]:text-white">
              Customers
            </TabsTrigger>
            <TabsTrigger value="leads" className="data-[state=active]:bg-[#1a365d] data-[state=active]:text-white">
              Enhancement Leads
            </TabsTrigger>
          </TabsList>

          {/* Bookings Tab */}
          <TabsContent value="bookings">
            <div className="bg-white border border-slate-200 p-6">
              {/* Filters */}
              <div className="flex flex-col md:flex-row gap-4 mb-6">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
                  <Input
                    type="text"
                    placeholder="Search by name or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 input-field"
                    data-testid="search-bookings"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full md:w-48 input-field" data-testid="filter-status">
                    <Filter size={16} className="mr-2" />
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Bookings Table */}
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="bookings-table">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 font-bold text-sm uppercase tracking-wider text-slate-500">Customer</th>
                      <th className="text-left py-3 px-4 font-bold text-sm uppercase tracking-wider text-slate-500">Service</th>
                      <th className="text-left py-3 px-4 font-bold text-sm uppercase tracking-wider text-slate-500">Date</th>
                      <th className="text-left py-3 px-4 font-bold text-sm uppercase tracking-wider text-slate-500">Status</th>
                      <th className="text-left py-3 px-4 font-bold text-sm uppercase tracking-wider text-slate-500">Amount</th>
                      <th className="text-left py-3 px-4 font-bold text-sm uppercase tracking-wider text-slate-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredBookings.map((booking) => (
                      <tr key={booking.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-4 px-4">
                          <div>
                            <p className="font-medium">{booking.customer_name}</p>
                            <p className="text-sm text-slate-500">{booking.customer_email}</p>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <span className="capitalize">
                            {booking.service_type.replace("_", " ")}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <div>
                            <p>{booking.preferred_date}</p>
                            <p className="text-sm text-slate-500">{booking.preferred_time}</p>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <span className={`px-2 py-1 text-xs font-medium rounded ${getStatusBadge(booking.status)}`}>
                            {booking.status.replace("_", " ").toUpperCase()}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <div>
                            <p className="font-medium">${booking.total_amount}</p>
                            {booking.deposit_paid && (
                              <p className="text-xs text-green-600">Deposit paid</p>
                            )}
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedBooking(booking);
                                setIsDetailDialogOpen(true);
                              }}
                              data-testid={`view-booking-${booking.id}`}
                            >
                              <Eye size={16} />
                            </Button>
                            <Select
                              value={booking.status}
                              onValueChange={(value) => updateBookingStatus(booking.id, value)}
                            >
                              <SelectTrigger className="w-32 h-8 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="pending">Pending</SelectItem>
                                <SelectItem value="confirmed">Confirmed</SelectItem>
                                <SelectItem value="in_progress">In Progress</SelectItem>
                                <SelectItem value="completed">Completed</SelectItem>
                                <SelectItem value="cancelled">Cancelled</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {filteredBookings.length === 0 && (
                  <div className="text-center py-12 text-slate-500">
                    No bookings found
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Customers Tab */}
          <TabsContent value="customers">
            <div className="bg-white border border-slate-200 p-6">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="customers-table">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 font-bold text-sm uppercase tracking-wider text-slate-500">Customer</th>
                      <th className="text-left py-3 px-4 font-bold text-sm uppercase tracking-wider text-slate-500">Phone</th>
                      <th className="text-left py-3 px-4 font-bold text-sm uppercase tracking-wider text-slate-500">Bookings</th>
                      <th className="text-left py-3 px-4 font-bold text-sm uppercase tracking-wider text-slate-500">Total Spent</th>
                      <th className="text-left py-3 px-4 font-bold text-sm uppercase tracking-wider text-slate-500">Last Booking</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customers.map((customer, index) => (
                      <tr key={index} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-4 px-4">
                          <div>
                            <p className="font-medium">{customer.customer_name}</p>
                            <p className="text-sm text-slate-500">{customer.email}</p>
                          </div>
                        </td>
                        <td className="py-4 px-4">{customer.customer_phone}</td>
                        <td className="py-4 px-4">{customer.booking_count}</td>
                        <td className="py-4 px-4 font-medium">${customer.total_spent}</td>
                        <td className="py-4 px-4 text-sm text-slate-500">
                          {customer.last_booking ? new Date(customer.last_booking).toLocaleDateString() : "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {customers.length === 0 && (
                  <div className="text-center py-12 text-slate-500">
                    No customers found
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Enhancement Leads Tab */}
          <TabsContent value="leads">
            <div className="bg-white border border-slate-200 p-6">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="leads-table">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 font-bold text-sm uppercase tracking-wider text-slate-500">Enhancement</th>
                      <th className="text-left py-3 px-4 font-bold text-sm uppercase tracking-wider text-slate-500">Customer</th>
                      <th className="text-left py-3 px-4 font-bold text-sm uppercase tracking-wider text-slate-500">Contact</th>
                      <th className="text-left py-3 px-4 font-bold text-sm uppercase tracking-wider text-slate-500">Status</th>
                      <th className="text-left py-3 px-4 font-bold text-sm uppercase tracking-wider text-slate-500">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {enhancementLeads.map((lead) => (
                      <tr key={lead.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-4 px-4">
                          <span className="capitalize font-medium">
                            {lead.enhancement_type.replace("_", " ")}
                          </span>
                        </td>
                        <td className="py-4 px-4">{lead.customer_name}</td>
                        <td className="py-4 px-4">
                          <div>
                            <p className="text-sm">{lead.customer_email}</p>
                            <p className="text-sm text-slate-500">{lead.customer_phone}</p>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <span className={`px-2 py-1 text-xs font-medium rounded ${
                            lead.status === "new" ? "bg-blue-100 text-blue-800" :
                            lead.status === "contacted" ? "bg-yellow-100 text-yellow-800" :
                            lead.status === "converted" ? "bg-green-100 text-green-800" :
                            "bg-slate-100 text-slate-800"
                          }`}>
                            {lead.status.toUpperCase()}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-sm text-slate-500">
                          {new Date(lead.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {enhancementLeads.length === 0 && (
                  <div className="text-center py-12 text-slate-500">
                    No enhancement leads found
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Booking Detail Dialog */}
      <Dialog open={isDetailDialogOpen} onOpenChange={setIsDetailDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="heading-md" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Booking Details
            </DialogTitle>
          </DialogHeader>
          {selectedBooking && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">Customer</span>
                  <p className="font-medium">{selectedBooking.customer_name}</p>
                </div>
                <div>
                  <span className="text-slate-500">Email</span>
                  <p className="font-medium">{selectedBooking.customer_email}</p>
                </div>
                <div>
                  <span className="text-slate-500">Phone</span>
                  <p className="font-medium">{selectedBooking.customer_phone}</p>
                </div>
                <div>
                  <span className="text-slate-500">Service</span>
                  <p className="font-medium capitalize">{selectedBooking.service_type.replace("_", " ")}</p>
                </div>
                <div>
                  <span className="text-slate-500">Garage Size</span>
                  <p className="font-medium capitalize">{selectedBooking.garage_size}</p>
                </div>
                <div>
                  <span className="text-slate-500">Status</span>
                  <span className={`px-2 py-1 text-xs font-medium rounded ${getStatusBadge(selectedBooking.status)}`}>
                    {selectedBooking.status.replace("_", " ").toUpperCase()}
                  </span>
                </div>
                <div>
                  <span className="text-slate-500">Date</span>
                  <p className="font-medium">{selectedBooking.preferred_date}</p>
                </div>
                <div>
                  <span className="text-slate-500">Time</span>
                  <p className="font-medium">{selectedBooking.preferred_time}</p>
                </div>
              </div>
              
              <div className="pt-4 border-t">
                <span className="text-slate-500 text-sm">Address</span>
                <p className="font-medium">{selectedBooking.customer_address}</p>
              </div>
              
              {selectedBooking.notes && (
                <div className="pt-4 border-t">
                  <span className="text-slate-500 text-sm">Notes</span>
                  <p>{selectedBooking.notes}</p>
                </div>
              )}
              
              <div className="pt-4 border-t flex justify-between">
                <div>
                  <span className="text-slate-500 text-sm">Total Amount</span>
                  <p className="text-2xl font-bold">${selectedBooking.total_amount}</p>
                </div>
                <div className="text-right">
                  <span className="text-slate-500 text-sm">Deposit</span>
                  <p className={`font-bold ${selectedBooking.deposit_paid ? "text-green-600" : "text-red-600"}`}>
                    {selectedBooking.deposit_paid ? "Paid ($100)" : "Not Paid"}
                  </p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPage;
